import type { EvHandoffJob, EvWorkflowSummary } from "../types/index.ts";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

interface EvHandoffStatusCardProps {
  workflow: EvWorkflowSummary;
  handoff: EvHandoffJob | null;
  queueing?: boolean;
  retrying?: boolean;
  onQueueHandoff?: () => void;
  onRetryHandoff?: () => void;
}

export function EvHandoffStatusCard({
  workflow,
  handoff,
  queueing = false,
  retrying = false,
  onQueueHandoff,
  onRetryHandoff,
}: EvHandoffStatusCardProps) {
  const notionUrl = handoff?.notionPageUrl;
  const driveUrl = handoff?.driveFolderUrl;

  return (
    <div className="ev-handoff-card">
      <div className="ev-handoff-card__header">
        <span className="section-title">Onboarding Package</span>
        <span
          className={`ev-handoff-card__status ev-handoff-card__status--${handoff?.status ?? "idle"}`}
        >
          {handoff?.status ?? "not queued"}
        </span>
      </div>

      <div className="ev-handoff-card__meta">
        <span>Stage: {workflow.stage.replaceAll("_", " ")}</span>
        <span>Template: {handoff?.targetTemplate ?? workflow.confirmedSaleType}</span>
      </div>

      <div className="ev-handoff-card__meta">
        <span>Queued: {formatDateTime(handoff?.createdAt ?? null)}</span>
        <span>Completed: {formatDateTime(handoff?.completedAt ?? null)}</span>
      </div>

      {handoff && (
        <div className="ev-handoff-card__meta">
          <span>Attempts: {handoff.attemptCount}</span>
          <span>Last retry: {formatDateTime(handoff.lastRetryAt)}</span>
        </div>
      )}

      {handoff && (
        <div className="ev-handoff-card__meta">
          <span>Last step: {handoff.lastExternalStep ?? "—"}</span>
          <span>Failure stage: {handoff.lastErrorStage ?? "—"}</span>
        </div>
      )}

      {handoff?.lastErrorCode && (
        <div className="ev-handoff-card__meta">
          <span>Error code: {handoff.lastErrorCode}</span>
        </div>
      )}

      {handoff?.lastError && <div className="ev-handoff-card__error">{handoff.lastError}</div>}

      <div className="ev-handoff-card__links">
        {notionUrl && (
          <a href={notionUrl} target="_blank" rel="noreferrer" className="btn btn-secondary">
            Open Notion
          </a>
        )}
        {driveUrl && (
          <a href={driveUrl} target="_blank" rel="noreferrer" className="btn btn-secondary">
            Open Drive
          </a>
        )}
      </div>

      <div className="ev-handoff-card__actions">
        {handoff?.status === "failed" ? (
          <button type="button" className="btn" disabled={retrying} onClick={onRetryHandoff}>
            {retrying ? "Retrying..." : "Retry onboarding package"}
          </button>
        ) : (
          <button type="button" className="btn" disabled={queueing} onClick={onQueueHandoff}>
            {queueing
              ? "Queueing..."
              : handoff
                ? "Queue onboarding package again"
                : "Create onboarding package"}
          </button>
        )}
      </div>
    </div>
  );
}
